// Formatowanie danych z feedu JDM.
//
// Backend zwraca obiekt aukcji 1:1 tak, jak publikuje go JDM — patrz
// backend/JDM_API_SPEC.md. Kody skrzyni i ocen stanu są japońskie, więc cała
// warstwa tłumaczenia na polski siedzi tutaj, wspólna dla listy i karty aukcji.

export const PLACEHOLDER =
  "https://ralfvanveen.com/wp-content/uploads/2021/06/Placeholder-_-Glossary.svg";

// Kody skrzyni z arkuszy aukcyjnych. Znaczenia IA/I5/SQ/5 są do potwierdzenia
// u dostawcy, dlatego nieznany kod pokazujemy w oryginale zamiast zgadywać.
const GEAR_LABELS = {
  AT: "Automatyczna",
  FA: "Automatyczna (lewarek podłogowy)",
  CA: "Automatyczna (lewarek przy kierownicy)",
  IA: "Automatyczna",
  MT: "Manualna",
  F5: "Manualna 5-biegowa",
  F6: "Manualna 6-biegowa",
  I5: "Manualna 5-biegowa",
  5: "Manualna 5-biegowa",
  SQ: "Sekwencyjna",
};

// Ocena stanu auta wystawiona przez rzeczoznawcę na aukcji.
const RATING_LABELS = {
  6: "Stan najwyższy",
  5: "Bardzo dobry",
  4.5: "Dobry",
  4: "Dobry, drobne ślady",
  3.5: "Przeciętny",
  3: "Wyraźne ślady zużycia",
  2: "Słaby",
  1: "Bardzo słaby",
  S: "Praktycznie nowe",
  R: "Po naprawie powypadkowej",
  R2: "Po naprawie powypadkowej",
  "***": "Brak oceny",
};

const STEERING_LABELS = {
  lhd: "Lewa (LHD)",
  rhd: "Prawa (RHD)",
};

// Opcje filtrów — tylko kody faktycznie występujące w feedzie, uporządkowane
// od najczęstszych. API filtruje po dokładnej wartości, więc każda pozycja to
// jeden kod JDM (nie da się wybrać "wszystkie automaty" jednym zapytaniem).
export const GEAR_OPTIONS = ["FA", "AT", "CA", "F6", "MT", "F5", "IA", "I5", "SQ", "5"];

export const RATING_OPTIONS = [
  "6", "5", "4.5", "4", "3.5", "3", "2", "1", "S", "R", "R2", "***",
];

// Legenda oznaczeń z arkusza aukcyjnego (spec_image). Rzeczoznawca nanosi te
// kody na schemat auta. Kody `X` i `R` znaczą co innego dla nadwozia niż dla
// szyb, dlatego legenda jest podzielona na sekcje.
export const SHEET_LEGEND = [
  {
    group: "Nadwozie i lakier",
    items: [
      ["A1", "Małe zadrapanie (rysa)"],
      ["A2", "Średnie zadrapanie (rysa)"],
      ["A3", "Duże zadrapanie (rysa)"],
      ["E1", "Mała wgniotka"],
      ["E2", "Średnia wgniotka"],
      ["E3", "Duża wgniotka"],
      ["U1", "Małe wgniecenie"],
      ["U1", "Mała wgniotka"],
      ["U3", "Duża wgniotka"],
      ["W1", "Naprawa lakieru mało widoczna (trudno wykrywalna)"],
      ["W2", "Naprawa lakieru średnio widoczna"],
      ["W3", "Naprawa lakieru bardzo widoczna (element musi zostać przemalowany)"],
      ["S1", "Rdza"],
      ["S2", "Duża ilość rdzy"],
      ["C1", "Korozja"],
      ["C2", "Dużo korozji"],
      ["P", "Plamy na lakierze"],
      ["H", "Lakier wyblakły"],
      ["X", "Element wymaga wymiany"],
      ["XX", "Element wymieniony"],
      ["B1", "Małe wgniecenie z zarysowaniem"],
      ["B2", "Wgniecenie z zadrapaniem"],
      ["B3", "Duże wgniecenie z zarysowaniem"],
      ["Y1", "Małe pęknięcie na lakierze"],
      ["Y2", "Średnie pęknięcie na lakierze"],
      ["Y3", "Duże pęknięcie na lakierze"],
    ],
  },
  {
    group: "Szyby",
    items: [
      ["X1", "Małe pęknięcie na przedniej szybie (około 1 cm)"],
      ["R", "Naprawione pęknięcie na przedniej szybie"],
      ["RX", "Naprawione pęknięcie na przedniej szybie (szyba wymaga wymiany)"],
      ["X", "Pęknięcie na przedniej szybie (wymaga wymiany)"],
      ["G", "Odprysk na szybie"],
    ],
  },
];

/** Wartość do wyświetlenia albo kreska, gdy JDM nic nie podał. */
export function orDash(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}

export function gearLabel(code) {
  if (!code) return "—";
  return GEAR_LABELS[code] || code;
}

export function ratingLabel(rating) {
  if (!rating) return "—";
  return RATING_LABELS[rating] || rating;
}

/** Auta po naprawie powypadkowej (R / R2) wymagają wyróżnienia w UI. */
export function isRepaired(rating) {
  return rating === "R" || rating === "R2";
}

export function steeringLabel(code) {
  if (!code) return "—";
  return STEERING_LABELS[code] || code;
}

/** Cena wywoławcza w JPY. `null` u ~15% aut — wtedy "Cena na zapytanie". */
export function formatPrice(jpy) {
  if (jpy === null || jpy === undefined) return null;
  return Number(jpy).toLocaleString("pl-PL") + " JPY";
}

/** Przebieg w km — JDM podaje już kilometry, bez przeliczania z mil. */
export function formatKm(km) {
  if (km === null || km === undefined) return "—";
  return Number(km).toLocaleString("pl-PL") + " km";
}

export function formatCc(cc) {
  if (cc === null || cc === undefined) return "—";
  return Number(cc).toLocaleString("pl-PL") + " cm³";
}

/** "2026-07-31 00:00:00" -> Date. Spacja zamiast "T" wywraca Safari. */
export function parseAuctionDate(value) {
  if (!value) return null;
  const date = new Date(String(value).replace(" ", "T"));
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Data aukcji bez godziny — feed zawsze podaje 00:00:00, więc pora dnia
 * jest nieznana i pokazywanie jej wprowadzałoby w błąd.
 */
export function formatAuctionDate(value) {
  const date = parseAuctionDate(value);
  if (!date) return "—";
  return date.toLocaleDateString("pl-PL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Ile dni do aukcji. Z tych danych da się policzyć tylko dni — feed nie
 * podaje godziny rozpoczęcia, więc odliczanie co do minuty byłoby zmyślone.
 */
export function daysUntilAuction(value) {
  const date = parseAuctionDate(value);
  if (!date) return null;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((startOfDay(date) - startOfDay(new Date())) / 86400000);
}

export function auctionCountdown(value) {
  const days = daysUntilAuction(value);
  if (days === null) return "—";
  if (days < 0) return "Zakończona";
  if (days === 0) return "Dziś";
  if (days === 1) return "Jutro";
  return `Za ${days} dni`;
}

export function auctionEnded(value) {
  const days = daysUntilAuction(value);
  return days === null || days < 0;
}

/**
 * Zdjęcia do galerii. `thumbnail` to osobny plik, którego nie ma w `gallery`,
 * więc dokładamy go na początek zamiast gubić.
 */
export function galleryPhotos(car) {
  const gallery = Array.isArray(car?.gallery) ? car.gallery : [];
  const photos = car?.thumbnail ? [car.thumbnail, ...gallery] : [...gallery];
  const unique = [...new Set(photos.filter(Boolean))];
  return unique.length ? unique : [PLACEHOLDER];
}
